import { Box, Button, CircularProgress, Typography } from '@mui/material';
import type { FC } from 'react';

import ErrorSection from 'components/ErrorSection/ErrorSection';
import Popup from 'components/Popup/Popup';
import SuccessSection from 'components/SuccessSection/SuccessSection';
import { useResponsive } from 'contexts/ResponsiveContext';
import { ModalSizeE } from 'declarations/enums/modal';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import type { NoopT } from 'declarations/types/common';

import useTicketApprovePopup from './hooks/useTicketApprovePopup';
import { getPopupContentStyle } from './TicketApprovePopup.styled';

type TicketApprovePopupPropsT = {
  ticketId: string;
  isOpen: boolean;
  onClose: NoopT;
};

const TicketApprovePopup: FC<TicketApprovePopupPropsT> = ({ ticketId, isOpen, onClose }) => {
  const { isMobile } = useResponsive();
  const {
    isApprovingTicket,
    isTicketSuccessfullyApproved,
    isTicketApprovingError,
    showPopupContent,
    handleApproveTicket,
    handleClosePopup,
    approveTicketErrorMessage,
    approveTicketValidationErrorMessage,
  } = useTicketApprovePopup();

  return (
    <Popup
      nested
      isOpen={isOpen}
      showCloseBtn
      modalTitle="Override"
      size={ModalSizeE.REQUEST_OVERRIDE_APPROVAL}
      popupId={ScrollLockIdE.TicketApprovePopup}
      contentStyle={getPopupContentStyle(isMobile)}
      onClose={handleClosePopup(onClose)}
      closeOnEscape={false}
    >
      {showPopupContent && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100%',
          }}
        >
          <Typography variant="body1" color="text.primary" align={isMobile ? 'center' : 'left'}>
            Are you sure you want to approve (override) this ticket?
          </Typography>
          <Box sx={{ flex: 1, minHeight: 16 }} />
          <Box
            display="flex"
            justifyContent={isMobile ? 'center' : 'flex-end'}
            sx={{ mt: 2, pt: 1 }}
          >
            <Button
              variant="contained"
              disabled={isApprovingTicket}
              onClick={handleApproveTicket([ticketId])}
              startIcon={
                isApprovingTicket ? <CircularProgress size={16} color="inherit" /> : undefined
              }
            >
              Approve
            </Button>
          </Box>
        </Box>
      )}

      {isTicketSuccessfullyApproved && (
        <Box sx={{ p: { xs: 0, md: '0 2.5rem 0.625rem' } }}>
          <SuccessSection message="Ticket successfully approved" />
        </Box>
      )}

      {isTicketApprovingError && (
        <Box sx={{ p: { xs: 0, md: '0 2.5rem 0.625rem' } }}>
          <ErrorSection
            errorDetails={approveTicketErrorMessage || approveTicketValidationErrorMessage}
          />
        </Box>
      )}
    </Popup>
  );
};

export default TicketApprovePopup;
